import React from 'react';
import { Calendar, Clock, MoreVertical } from 'lucide-react';
import { BookingListItem, BookingStatus } from '../../model/professional/booking-model';
import { t } from 'i18next';

// Type pour adapter le statut de l'API à l'UI
type UIStatus = BookingStatus | 'canceled';

// Nous utilisons directement BookingListItem au lieu de Reservation

interface StatusInfo {
  icon: React.ReactNode;
  color: string;
  label: string;
}

interface ReservationGridProps {
  reservations: BookingListItem[];
  onShowDetails: (id: string) => void;
  getStatusInfo: (status: UIStatus | BookingStatus) => StatusInfo;
  formatDate: (dateString: string) => string;
  formatTime: (dateString: string) => string;
}

const ReservationGrid: React.FC<ReservationGridProps> = ({
  reservations,
  onShowDetails,
  getStatusInfo,
  formatDate,
  formatTime
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {reservations.map((reservation) => {
        // Adapter le statut pour l'UI
        let uiStatus: UIStatus | BookingStatus = reservation.status;
        if (reservation.status === 'cancelled') {
          uiStatus = 'canceled' as UIStatus;
        }
        const statusInfo = getStatusInfo(uiStatus);
        
        return (
          <div
            key={reservation.id}
            className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer ${
              reservation.status === 'cancelled' || reservation.status === 'completed' ? 'opacity-60' : ''
            }`}
            onClick={() => onShowDetails(reservation.id)}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                {/* <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600"
                  checked={selectedReservations.includes(reservation.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleReservation(reservation.id);
                  }}
                /> */}
                <Calendar size={16} className="text-gray-500" />
                <span className="text-sm font-medium">{reservation.startTime ? formatDate(reservation.startTime) : (reservation.createdAt ? formatDate(reservation.createdAt) : t('reservations.dateUnknown', 'Date inconnue'))}</span>
              </div>
              <button 
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowDetails(reservation.id);
                }}
              >
                <MoreVertical size={16} />
              </button>
            </div>

            <div className="space-y-2">
              <div>
                <h3 className="font-medium">{`${reservation.clientFirstName} ${reservation.clientLastName}`}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {reservation.services && reservation.services.length > 0
                    ? reservation.services.map(s => s.serviceName).join(', ')
                    : reservation.serviceName}
                </p>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                  <Clock size={14} />
                  <span>
                    {reservation.startTime ? (
                      `${formatTime(reservation.startTime)} - ${formatTime(reservation.endTime)}`
                    ) : (
                      t('reservations.timeUnknown', 'Sans créneau')
                    )}
                  </span>
                </div>
                <span className="font-medium">{`${reservation.totalPrice}€`}</span>
              </div>

              <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs ${statusInfo.color}`}>
                  <span className="mr-1">{statusInfo.icon}</span>
                  {statusInfo.label}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      {reservations.length === 0 && (
        <div className="col-span-full text-center py-6 text-gray-500 dark:text-gray-400">
          {t('reservations.noReservationsFound', 'Aucune réservation trouvée')}
        </div>
      )}
    </div>
  );
};

export default ReservationGrid;
